import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

/** Что сообщаем: сделано (зелёное) или не вышло (красное, висит дольше). */
export type MessageKind = "ok" | "error";

/**
 * Снек-бар: выезжает сверху, сам уезжает. Строка — «сделано»; Error (ошибка
 * запроса или проверки) — «не вышло», с тем, что нужно исправить. Явный вид
 * вторым аргументом — для строк-предупреждений вроде «Укажите глаз».
 */
export interface MessageOptions {
  /** Кнопка в снеке — «Отменить» после удаления и т. п. */
  action?: { label: string; run: () => void | Promise<void> };
  /** Сколько держать на экране, мс (по умолчанию — по виду). */
  ms?: number;
}

export type ShowMessage = (text: string | Error, kind?: MessageKind, opts?: MessageOptions) => void;

const MessageCtx = createContext<ShowMessage>(() => {});

export const useMessage = () => useContext(MessageCtx);

const HOLD: Record<MessageKind, number> = { ok: 2800, error: 6000 };

export function MessageProvider({ children }: { children: React.ReactNode }) {
  const [snack, setSnack] = useState<{
    text: string;
    kind: MessageKind;
    n: number;
    action?: MessageOptions["action"];
    ms?: number;
  } | null>(null);
  const [shown, setShown] = useState(false);
  const hide = useRef<number | null>(null);
  const clear = useRef<number | null>(null);

  const dismiss = useCallback(() => {
    if (hide.current) window.clearTimeout(hide.current);
    setShown(false);
    // Дать уехать, потом убрать из DOM.
    if (clear.current) window.clearTimeout(clear.current);
    clear.current = window.setTimeout(() => setSnack(null), 360);
  }, []);

  const show = useCallback<ShowMessage>((input, kind, opts) => {
    const text = input instanceof Error ? input.message : input;
    const k: MessageKind = kind ?? (input instanceof Error ? "error" : "ok");
    if (!text) return;
    if (clear.current) window.clearTimeout(clear.current);
    setSnack((prev) => ({ text, kind: k, n: (prev?.n ?? 0) + 1, action: opts?.action, ms: opts?.ms }));
  }, []);

  // Новое сообщение: показать (с кадра, чтобы сработал transition) и завести
  // таймер ухода; повтор перезапускает таймер.
  useEffect(() => {
    if (!snack) return;
    const raf = window.requestAnimationFrame(() => setShown(true));
    if (hide.current) window.clearTimeout(hide.current);
    hide.current = window.setTimeout(dismiss, snack.ms ?? HOLD[snack.kind]);
    return () => window.cancelAnimationFrame(raf);
  }, [snack, dismiss]);

  return (
    <MessageCtx.Provider value={show}>
      {snack && (
        <div
          className={`snack snack-${snack.kind}${shown ? " snack-show" : ""}`}
          role={snack.kind === "error" ? "alert" : "status"}
        >
          <i className="snack-icon" aria-hidden="true">
            {snack.kind === "error" ? "!" : "✓"}
          </i>
          <div className="snack-text">
            {snack.kind === "error" && <b>Не сохранено</b>}
            {snack.text}
          </div>
          {snack.action && (
            <span
              className="snack-action"
              onClick={() => {
                const run = snack.action!.run;
                dismiss();
                void run();
              }}
            >
              {snack.action.label}
            </span>
          )}
          <span className="snack-close" title="Закрыть" onClick={dismiss}>
            ×
          </span>
        </div>
      )}
      {children}
    </MessageCtx.Provider>
  );
}
